import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { resolveUserAvatarUrl } from "../../api/apiConfig/apiConfig";
import { ProfileContext } from "../../context/ProfileProvider";
import { openKycVerification } from "../../ui/Pages/KycVara/openKycVerification";
import AuthService from "../../api/services/AuthService";
import { BUY_CRYPTO_SUBMENU_ITEMS } from "../headerBuyCryptoSubmenu";
import { TRADE_SUBMENU_ITEMS } from "../headerTradeSubmenu";
import { FUTURES_SUBMENU_ITEMS } from "../headerFuturesSubmenu";
import { EARNING_SUBMENU_ITEMS } from "../headerEarningSubmenu";
import { MORE_SUBMENU_ITEMS } from "../headerMoreSubmenu";
import { OTC_FORM_PATH, OTC_NAV_LABEL, isOtcNavActive } from "../headerInstitutionalSubmenu";
import { ABOUT_SUBMENU_ITEMS } from "../headerAboutSubmenu";
import { isMoreMenuPlaceholderDestination, isNavPathActive, isAnySubmenuActive } from "../headerNavActive";
import { alertSuccessMessage } from "../CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../networkConfig";
import { logoutCurrentSession } from "../../utils/sessionLogout";

/** Header Order hover menu — routes/labels aligned with `OrderSidebar`. */
const HEADER_ORDER_MENU_ITEMS = [
  { to: "/user_profile/orders/spot_orders", label: "Spot Order", page: "Spot Order", iconSrc: "/images/security/order_hdr_icon.svg", iconSrcLight: "/images/security/order_hdr_icon_light.svg" },
  { to: "/user_profile/orders/futures_open_orders", label: "Futures", page: "Futures Open Order", iconSrc: "/images/security/order_hdr_icon2.svg", iconSrcLight: "/images/security/order_hdr_icon2_light.svg" },
  { to: "/user_profile/orders/margin_orders", label: "Margin Account", page: "Margin Orders", iconSrc: "/images/security/order_hdr_icon3.svg", iconSrcLight: "/images/security/order_hdr_icon3_light.svg" },
  { to: "/user_profile/orders/otc_trade", label: "OTC", page: "OTC", iconSrc: "/images/security/order_hdr_icon4.svg", iconSrcLight: "/images/security/order_hdr_icon4_light.svg" },
  // { to: "/user_profile/orders/convert_orders", label: "Convert", page: "Convert", iconSrc: "/images/security/order_hdr_icon4.svg" },
  // { to: "/user_profile/orders/launchpad_orders", label: "Launchpad", page: "Launchpad", iconSrc: "/images/security/order_hdr_icon5.svg" },
  // { to: "/user_profile/orders/staking_orders", label: "Earning", page: "Earning", iconSrc: "/images/security/order_hdr_icon6.svg" },
  // { to: "/user_profile/orders/copy_trading_orders", label: "Copy Trading", page: "Copy Trading", iconSrc: "/images/security/order_hdr_icon7.svg" },
  // { to: "/user_profile/orders/fiat_asset_order", label: "Fiat Order", page: "Fiat Asset Order", iconSrc: "/images/security/order_hdr_icon8.svg" },
  { to: "/user_profile/orders/deposit_history", label: "Asset History", page: "Transaction History", iconSrc: "/images/security/order_hdr_icon9.svg", iconSrcLight: "/images/security/order_hdr_icon9_light.svg" },
  // { to: "/user_profile/orders/quick_buy_sell", label: "Quick Buy/Sell", page: "Quick Buy/Sell", iconSrc: "/images/security/order_hdr_icon10.svg" },
];

/** Assets dropdown rows — same routes as `WalletSidebar` (`wallet/:walletType` + asset overview). */
const HEADER_ASSETS_WALLET_LINKS = [
  { key: "overview", label: "Overview", to: "/user_profile/asset_overview", iconSrc: "/images/security/pocket_icon.svg", iconSrcLight: "/images/security/pocket_icon_light.svg" },
  { key: "spot", label: "Spot Wallet", to: "/user_profile/wallet/spot", iconSrc: "/images/security/pocket_icon2.svg", iconSrcLight: "/images/security/pocket_icon2_light.svg" },
  { key: "main", label: "Main Wallet", to: "/user_profile/wallet/main", iconSrc: "/images/security/pocket_icon3.svg", iconSrcLight: "/images/security/pocket_icon3_light.svg" },
  { key: "margin", label: "Isolated Margin Wallet", to: "/user_profile/wallet/margin", iconSrc: "/images/security/pocket_icon4.svg", iconSrcLight: "/images/security/pocket_icon4_light.svg" },
  { key: "cross_margin", label: "Cross Margin Wallet", to: "/user_profile/wallet/cross_margin", iconSrc: "/images/security/pocket_icon5.svg", iconSrcLight: "/images/security/pocket_icon5_light.svg" },
  // { key: "p2p", label: "P2P Wallet", to: "/user_profile/wallet/p2p", iconSrc: "/images/security/pocket_icon4.svg" },
  { key: "futures", label: "Futures Wallet", to: "/user_profile/wallet/futures", iconSrc: "/images/security/pocket_icon5.svg", iconSrcLight: "/images/security/pocket_icon5_light.svg" },
  { key: "options", label: "Options Wallet", to: "/user_profile/wallet/options", iconSrc: "/images/security/pocket_icon4.svg", iconSrcLight: "/images/security/pocket_icon4_light.svg" },
  // { key: "staking", label: "Staking", to: "/user_profile/staking", iconSrc: "/images/security/order_hdr_icon6.svg" },
  // { key: "swap", label: "Swap Wallet", to: "/user_profile/wallet/swap", iconSrc: "/images/security/pocket_icon2.svg" },
  // { key: "earning", label: "Earning Wallet", to: "/user_profile/wallet/earning", iconSrc: "/images/security/pocket_icon5.svg" },
];

/** Same query as asset overview: `GET .../estimated-portfolio?walletType=` (empty = all / default). */
const HEADER_ASSETS_OVERVIEW_WALLET_TYPE = "";

const formatHeaderOverviewUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

function truncateNotificationPreview(str, maxLen) {
  const s = str != null ? String(str).trim() : "";
  if (!s || s.length <= maxLen) return s || "—";
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

const maskEmail = (email) => {
  if (!email || typeof email !== "string") return "--";
  const [localPart, domainPart] = email.split("@");
  if (!localPart || !domainPart) return email;
  if (localPart.length <= 2) return `${localPart[0] || "*"}***@${domainPart}`;
  return `${localPart[0]}***${localPart[localPart.length - 1]}@${domainPart}`;
};

const AuthHeader = () => {
  const {
    setCurrentPage,
    notifications,
    notificationCounts,
    estimatedPortfolio,
    fetchEstimatedPortfolio,
    fetchNotifications,
    userDetails,
    handleTheme,
    setThemeUpdated,
    newStoredTheme,
  } = useContext(ProfileContext);

  const maskedProfileEmail = maskEmail(userDetails?.emailId || "");
  const profileUid = userDetails?.uuid != null && userDetails?.uuid !== "" ? String(userDetails.uuid) : "--";

  const handleCopyProfileUid = useCallback(async () => {
    if (!profileUid || profileUid === "--") return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileUid);
        alertSuccessMessage("UID copied to clipboard");
        return;
      }
    } catch {
      // Fallback below if clipboard API is unavailable or blocked.
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = profileUid;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      // Silent fail to avoid UI interruption.
    }
  }, [profileUid]);

  const [showBalance, setShowBalance] = useState(true);
  const [searchPair, setSearchPair] = useState("");
  const [allPairs, setAllPairs] = useState([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsFetched, setPairsFetched] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const openAsserOverview = () => {
    closeNavbar();
    navigate(`/user_profile/asset_overview`);
  };

  // Check if current page is an auth page (no nav should be active)
  const isAuthPage = ['/login', '/signup', '/forgot_password'].some(
    path => location.pathname.startsWith(path)
  );

  // Segment-safe active check (avoid `includes()` false positives and shared `/coming_soon` collisions)
  const isActive = (path, exact = true) => {
    if (isAuthPage) return false;
    return isNavPathActive(location.pathname, path, exact);
  };

  const isMoreMenuActiveRoute = MORE_SUBMENU_ITEMS.some(
    (item) => !isMoreMenuPlaceholderDestination(item.to) && isActive(item.to, false),
  );
  const isOtcExecutionNavActive = isOtcNavActive(location.pathname);

  const isAboutMenuActiveRoute = isAnySubmenuActive(location.pathname, ABOUT_SUBMENU_ITEMS) ||
    isActive("/about_us", false);

  const isFuturesNavActive =
    isAnySubmenuActive(location.pathname, FUTURES_SUBMENU_ITEMS) ||
    (location.pathname === "/coming_soon" && new URLSearchParams(location.search).get("from") === "futures");

  // Check if Dashboard should be active (user_profile pages except swap)
  const isAssetsActive = location.pathname.startsWith('/user_profile/asset_overview') || location.pathname.startsWith('/user_profile/wallet');
  const isOrdersActive = location.pathname.startsWith('/user_profile/orders');

  const isDashboardActive = location.pathname.startsWith('/user_profile') &&
    location.pathname !== '/convert';

  const logOut = () => {
    void (async () => {
      await logoutCurrentSession();
      navigate("/", { replace: true, state: null });
    })();
  };

  // Fetch pairs from API when modal opens
  const fetchPairs = useCallback(async () => {
    if (pairsFetched || pairsLoading) return;
    setPairsLoading(true);
    try {
      const result = await AuthService.getPairs();
      if (result?.success) {
        setAllPairs(result.data || []);
        setPairsFetched(true);
      }
    } catch (error) {
    } finally {
      setPairsLoading(false);
    }
  }, [pairsFetched, pairsLoading]);

  // Filter pairs based on search
  const filteredPairs = useMemo(() => {
    if (!searchPair) return allPairs;
    return allPairs.filter((item) =>
      item?.base_currency?.toLowerCase()?.includes(searchPair?.toLowerCase())
    );
  }, [searchPair, allPairs]);

  const overviewEstimatedUsdt = useMemo(() => {
    const ep = estimatedPortfolio || {};
    return ep.dollarPrice ?? ep.estimated_total_usdt ?? ep.estimatedTotalUsdt ?? ep.estimated_total;
  }, [estimatedPortfolio]);

  const overviewBalanceNum = useMemo(() => {
    if (!showBalance) return "—";
    return formatHeaderOverviewUsdt(overviewEstimatedUsdt);
  }, [showBalance, overviewEstimatedUsdt]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    void fetchEstimatedPortfolio(HEADER_ASSETS_OVERVIEW_WALLET_TYPE);
  }, [fetchEstimatedPortfolio]);

  const [isOpenNav, setIsOpenNav] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileProfileSection, setMobileProfileSection] = useState(null); // "assets" | "orders" | null
  const [showNotification, setShowNotification] = useState(false);
  const dropdownRef = useRef(null);
  /** Whole auth header (nav + logo + header_right). Hover dropdowns live outside `dropdownRef` navbar only. */
  const headerRef = useRef(null);
  const notificationRef = useRef(null);
  const dropdownCloseTimerRef = useRef(null);
  const notificationCloseTimerRef = useRef(null);
  const isPinnedByClickRef = useRef(null);
  const lastHoverOpenTimeRef = useRef(0);
  const headerNavScrollRef = useRef(null);
  const [headerNavScrollUi, setHeaderNavScrollUi] = useState({ enabled: false, canLeft: false, canRight: false });

  const toggleNavbar = () => setIsOpenNav(!isOpenNav);

  const openDropdownHover = (key) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    if (window.innerWidth >= 1200) {
      lastHoverOpenTimeRef.current = Date.now();
      setOpenDropdown(key);
    }
  };

  const closeDropdownHover = () => {
    if (window.innerWidth < 1200) return;
    // If pinned by click, do not close via mouse leave
    if (isPinnedByClickRef.current) return;
    if (dropdownCloseTimerRef.current) clearTimeout(dropdownCloseTimerRef.current);
    dropdownCloseTimerRef.current = setTimeout(() => {
      isPinnedByClickRef.current = null;
      setOpenDropdown(null);
    }, 350);
  };

  const openNotificationHover = () => {
    if (notificationCloseTimerRef.current) {
      clearTimeout(notificationCloseTimerRef.current);
      notificationCloseTimerRef.current = null;
    }
    if (window.innerWidth >= 1200) setShowNotification(true);
  };

  const closeNotificationHover = () => {
    if (window.innerWidth < 1200) return;
    notificationCloseTimerRef.current = setTimeout(() => setShowNotification(false), 300);
  };

  const closeNavbar = () => {
    isPinnedByClickRef.current = null;
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    setIsOpenNav(false);
    setOpenDropdown(null);
    setShowNotification(false);
  };

  const toggleDropdown = (key) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    if (window.innerWidth < 1200) {
      setOpenDropdown((prev) => (prev === key ? null : key));
      return;
    }
    // Desktop:
    // If the menu was already open and pinned, or open for a while (> 350ms): toggle close it
    const timeSinceHover = Date.now() - (lastHoverOpenTimeRef.current || 0);
    if (openDropdown === key && (isPinnedByClickRef.current === key || timeSinceHover > 350)) {
      isPinnedByClickRef.current = null;
      setOpenDropdown(null);
    } else {
      // Pin open on click so moving mouse towards/inside menu never closes it
      isPinnedByClickRef.current = key;
      setOpenDropdown(key);
    }
  };

  useEffect(() => {
    return () => {
      if (dropdownCloseTimerRef.current) clearTimeout(dropdownCloseTimerRef.current);
      if (notificationCloseTimerRef.current) clearTimeout(notificationCloseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (openDropdown === "wallet" || mobileProfileSection === "assets") {
      void fetchEstimatedPortfolio(HEADER_ASSETS_OVERVIEW_WALLET_TYPE);
    }
  }, [openDropdown, mobileProfileSection, fetchEstimatedPortfolio]);

  useEffect(() => {
    const el = headerNavScrollRef.current;
    if (!el) return;

    const mq = window.matchMedia("(min-width: 1200px) and (max-width: 1367px)");

    const recompute = () => {
      const enabled = mq.matches;
      if (!enabled) {
        setHeaderNavScrollUi({ enabled: false, canLeft: false, canRight: false });
        return;
      }
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const canLeft = el.scrollLeft > 1;
      const canRight = el.scrollLeft < maxScrollLeft - 1;
      setHeaderNavScrollUi({ enabled: true, canLeft, canRight });
    };

    const onScroll = () => recompute();
    const onResize = () => recompute();

    recompute();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    mq.addEventListener?.("change", recompute);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      mq.removeEventListener?.("change", recompute);
    };
  }, []);

  useEffect(() => {
    isPinnedByClickRef.current = null;
    setIsOpenNav(false);
    setOpenDropdown(null);
    setMobileProfileSection(null);
  }, [location.pathname]);

  // Outside click handler (desktop + mobile) – close nav dropdown and notification panel
  useEffect(() => {
    const handlePointerOutside = (event) => {
      // Right / middle mouse: do not close hover menus (context menu, etc.)
      if (event.type === "mousedown" && event.button !== 0) return;

      const target = event.target;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpenNav(false);
      }
      // Hover dropdowns (wallet / profile / order) are NOT inside navbar `dropdownRef` — use full header
      if (headerRef.current && !headerRef.current.contains(target)) {
        isPinnedByClickRef.current = null;
        setOpenDropdown(null);
      }
      // Notification: close only when click is OUTSIDE both icon and panel content (content par click = close mat karo)
      const isInsideNotification = notificationRef.current?.contains(target) ||
        target.closest?.('.header_notification');
      if (!isInsideNotification) {
        setShowNotification(false);
      }
    };

    document.addEventListener("mousedown", handlePointerOutside);
    document.addEventListener("touchstart", handlePointerOutside);

    return () => {
      document.removeEventListener("mousedown", handlePointerOutside);
      document.removeEventListener("touchstart", handlePointerOutside);
    };
  }, []);

  const [depositSidebarOpen, setDepositSidebarOpen] = useState(false);

  const toggleDepositSidebar = () => {
    setDepositSidebarOpen((v) => !v);
  };

  const closeDepositSidebar = () => {
    setDepositSidebarOpen(false);
  };

  const goOrdersIndexFromHeader = () => {
    closeNavbar();
    closeDepositSidebar();
    setCurrentPage("Open Order");
    navigate("/user_profile/orders/spot_orders");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("#depositSidebar, .deposit-btn")) {
        closeDepositSidebar();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [openSection, setOpenSection] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const nextPage = (data) => {
    localStorage.setItem("RecentPair", JSON.stringify(data));
    navigate(`/trade/${data?.base_currency}_${data?.quote_currency}`);
    window.location.reload();
  };

  const handleHeaderNotifyItemNavigate = useCallback(
    async (item) => {
      if (!DISABLE_ALL_NETWORK && item?._id && !item?.isSeen) {
        try {
          const res = await AuthService.markasRead(String(item._id));
          if (res?.success === true || res?.success === 1) fetchNotifications?.();
        } catch {
          /* silent */
        }
      }
      navigate("/user_profile/notification");
      setCurrentPage("Notification");
      setShowNotification(false);
    },
    [fetchNotifications, navigate, setCurrentPage],
  );

  const handleNavClick = (e, hashId) => {
    e.preventDefault();
    closeNavbar();
    navigate(`/#${hashId}`);
    // Scroll after a tick so the section is in the DOM
    setTimeout(() => {
      const el = document.getElementById(hashId);
      if (el) {
        const headerHeight = document.querySelector("header")?.offsetHeight || 80;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - headerHeight, behavior: "smooth" });
      }
    }, 50);
  };

  return (
    <>
      <header ref={headerRef} className="sticky-top afterloginheader">
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-2 logo_s">
              <div className="logo "><Link to="/">
                <img className='mobile_logo' src="/images/argb_mobile-logo.svg" alt="logo" />
                <img className='darkogo' src="/images/logo_light.svg" alt="logo" />
              </Link></div>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-6 navigation_s">
              <div className="navigation">
                <nav className="navbar navbar-expand-lg" ref={dropdownRef}>
                  <div className="container-fluid">
                    {/* Toggle Button */}
                    <button
                      className="navbar-toggler"
                      type="button"
                      onClick={toggleNavbar}
                      aria-controls="mainNavbar"
                      aria-expanded={isOpenNav}
                      aria-label="Toggle navigation"
                    >
                      <span className="navbar-toggler-icon">
                        <img src="/images/toggle_icon.svg" alt="Toggle" />
                      </span>
                    </button>

                    {/* Navbar Links */}
                    <div className={`collapse navbar-collapse ${isOpenNav ? "show" : ""}`} id="mainNavbar">
                      <button
                        type="button"
                        className="main-navbar__close d-xl-none"
                        onClick={closeNavbar}
                        aria-label="Close menu"
                      >
                        <i className="ri-close-line" aria-hidden="true" />
                      </button>
                      <div className="header-nav-scroll">
                        <button
                          type="button"
                          className={`header-nav-scroll__arrow header-nav-scroll__arrow--left${headerNavScrollUi.enabled && headerNavScrollUi.canLeft ? "" : " is-hidden"}`}
                          aria-label="Scroll left"
                          onClick={() => {
                            const el = headerNavScrollRef.current;
                            if (!el) return;
                            el.scrollBy({ left: -Math.max(220, el.clientWidth * 0.55), top: 0, behavior: "smooth" });
                          }}
                        >
                          <i className="ri-arrow-left-s-line" aria-hidden="true" />
                        </button>

                        <div className="header-nav-scroll__viewport" ref={headerNavScrollRef}>
                          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                            <li className="nav-item">
                              <Link
                                className={`nav-link ${isActive("/") && !location.hash ? "active" : ""}`}
                                to="/"
                                onClick={(e) => {
                                  closeNavbar();
                                  navigate("/");
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                              >
                                Home
                              </Link>
                            </li>
                            <li className="nav-item">
                              <Link
                                className={`nav-link ${isActive("/") && location.hash === "#features" ? "active" : ""}`}
                                to="/#features"
                                onClick={(e) => handleNavClick(e, "features")}
                              >
                                Features
                              </Link>
                            </li>
                            <li className="nav-item">
                              <Link
                                className={`nav-link ${isActive("/") && location.hash === "#why-arab" ? "active" : ""}`}
                                to="/#why-arab"
                                onClick={(e) => handleNavClick(e, "why-arab")}
                              >
                                Why Arab Global
                              </Link>
                            </li>
                            {/* About Us Dropdown */}
                            <li
                              className={`nav-item dropdown ${isAboutMenuActiveRoute || openDropdown === "about" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("about")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isAboutMenuActiveRoute || openDropdown === "about" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("about")}
                              >
                                About Us
                              </span>
                              <ul
                                className={`dropdown-menu dropdown-menu--about ${openDropdown === "about" ? "show" : ""}`}
                                onMouseEnter={() => openDropdownHover("about")}
                                onMouseLeave={closeDropdownHover}
                              >
                                {ABOUT_SUBMENU_ITEMS.map((item) => (
                                  <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                    <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                      <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                      <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                      <span className="dropdown-item-content">
                                        <span className="dropdown-item-text">{item.title}</span>
                                        <span className="dropdown-item-desc">{item.desc}</span>
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="nav-item">
                              <Link
                                className={`nav-link ${isActive("/") && location.hash === "#support" ? "active" : ""}`}
                                to="/#support"
                                onClick={(e) => handleNavClick(e, "support")}
                              >
                                Support
                              </Link>
                            </li>



                            <li className="nav-item">
                              <Link
                                className={`nav-link ${isActive("/market") ? "active" : ""}`}
                                to="/market"
                                onClick={closeNavbar}
                              >
                                Market
                              </Link>
                            </li>

                            {/* Trade Dropdown */}
                            <li
                              className={`nav-item dropdown ${isAnySubmenuActive(location.pathname, TRADE_SUBMENU_ITEMS) || openDropdown === "trade" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("trade")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isAnySubmenuActive(location.pathname, TRADE_SUBMENU_ITEMS) || openDropdown === "trade" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("trade")}
                              >
                                Trade
                              </span>
                              <ul
                                className={`dropdown-menu dropdown-menu--trade ${openDropdown === "trade" ? "show" : ""}`}
                                onMouseEnter={() => openDropdownHover("trade")}
                                onMouseLeave={closeDropdownHover}
                              >
                                {TRADE_SUBMENU_ITEMS.map((item) => (
                                  <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                    <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                      <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                      <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                      <span className="dropdown-item-content">
                                        <span className="dropdown-item-text">{item.title}</span>
                                        <span className="dropdown-item-desc">{item.desc}</span>
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>

                            {/* Futures Dropdown */}
                            <li
                              className={`nav-item dropdown ${isFuturesNavActive || openDropdown === "futures" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("futures")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isFuturesNavActive || openDropdown === "futures" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("futures")}
                              >
                                Futures
                              </span>
                              <ul
                                className={`dropdown-menu dropdown-menu--futures ${openDropdown === "futures" ? "show" : ""}`}
                                onMouseEnter={() => openDropdownHover("futures")}
                                onMouseLeave={closeDropdownHover}
                              >
                                {FUTURES_SUBMENU_ITEMS.map((item) => (
                                  <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                    <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                      <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                      <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                      <span className="dropdown-item-content">
                                        <span className="dropdown-item-text">{item.title}</span>
                                        <span className="dropdown-item-desc">{item.desc}</span>
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>

                            {/* Earning Dropdown */}
                            <li
                              className={`nav-item dropdown ${isAnySubmenuActive(location.pathname, EARNING_SUBMENU_ITEMS) || openDropdown === "earning" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("earning")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isAnySubmenuActive(location.pathname, EARNING_SUBMENU_ITEMS) || openDropdown === "earning" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("earning")}
                              >
                                Earning
                              </span>
                              <ul
                                className={`dropdown-menu dropdown-menu--earning ${openDropdown === "earning" ? "show" : ""}`}
                                onMouseEnter={() => openDropdownHover("earning")}
                                onMouseLeave={closeDropdownHover}
                              >
                                {EARNING_SUBMENU_ITEMS.map((item) => (
                                  <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                    <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                      <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                      <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                      <span className="dropdown-item-content">
                                        <span className="dropdown-item-text">{item.title}</span>
                                        <span className="dropdown-item-desc">{item.desc}</span>
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>

                            {/* <li className={`nav-item vip-otc-nav ${isOtcExecutionNavActive ? "active" : ""}`}>
                              <Link
                                className={`nav-link ${isOtcExecutionNavActive ? "active" : ""}`}
                                to={OTC_FORM_PATH}
                                onClick={closeNavbar}
                              >
                                {OTC_NAV_LABEL}
                              </Link>
                            </li> */}

                            {/* More Dropdown */}
                            <li
                              className={`nav-item dropdown ${isMoreMenuActiveRoute || openDropdown === "more" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("more")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isMoreMenuActiveRoute || openDropdown === "more" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("more")}
                              >
                                More
                              </span>
                              <ul
                                className={`dropdown-menu dropdown-menu--more ${openDropdown === "more" ? "show" : ""}`}
                                onMouseEnter={() => openDropdownHover("more")}
                                onMouseLeave={closeDropdownHover}
                              >
                                {MORE_SUBMENU_ITEMS.map((item) => (
                                  <li key={item.id} className={item.className || (item.id ? `${item.id}-item` : "")}>
                                    <Link className="dropdown-item" to={item.to} onClick={closeNavbar}>
                                      <img className="nav_submenu_icon dark_img" src={item.iconSrcDark} alt="" width={44} height={44} loading="lazy" />
                                      <img className="nav_submenu_icon light_img" src={item.iconSrcLight} alt="" width={44} height={44} loading="lazy" />
                                      <span className="dropdown-item-content">
                                        <span className="dropdown-item-text">
                                          {item.title}
                                          {item.titleSub ? <sub className="nav-title-badge--new">{item.titleSub}</sub> : null}
                                        </span>
                                        <span className="dropdown-item-desc">{item.desc}</span>
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>

                            {/* <li
                              className={`nav-item dropdown mbl ${isActive("/earning") || isActive("/referral") || openDropdown === "download" ? "active" : ""}`}
                              onMouseEnter={() => openDropdownHover("download")}
                              onMouseLeave={closeDropdownHover}
                            >
                              <span
                                className={`nav-link dropdown-toggle ${isActive("/earning") || isActive("/referral") || openDropdown === "download" ? "active" : ""}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleDropdown("download")}
                              >
                                <img src="/images/download_icon2.svg" alt="scan" width={12} /> Download
                              </span>
                              <ul className={`dropdown-menu ${openDropdown === "download" ? "show" : ""}`} onMouseEnter={() => openDropdownHover("download")} onMouseLeave={closeDropdownHover}>
                                <li>
                                  <div className='qrcode'>
                                    <div className="scan_img"><img src="/images/scan.png" alt="scan" /></div>
                                    <p>Scan to Download App iOS & Android</p>
                                    <button className='btn'>Download</button>
                                  </div>
                                </li>
                              </ul>
                            </li> */}
                            <li className="nav-item mbl nsvqrcode">
                              <Link
                                className="nav-link"
                                to="/#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleTheme();
                                  setThemeUpdated((prev) => !prev);
                                  closeNavbar();
                                }}
                                role="button"
                                aria-label="Toggle theme"
                              >
                                Theme <span>
                                  <img
                                    src={newStoredTheme === "dark" ? "/images/themeicon.svg" : "/images/themeicon.svg"}
                                    alt="theme"
                                  />
                                </span>
                              </Link>
                            </li>
                          </ul>
                        </div>

                        <button
                          type="button"
                          className={`header-nav-scroll__arrow header-nav-scroll__arrow--right${headerNavScrollUi.enabled && headerNavScrollUi.canRight ? "" : " is-hidden"}`}
                          aria-label="Scroll right"
                          onClick={() => {
                            const el = headerNavScrollRef.current;
                            if (!el) return;
                            el.scrollBy({ left: Math.max(220, el.clientWidth * 0.55), top: 0, behavior: "smooth" });
                          }}
                        >
                          <i className="ri-arrow-right-s-line" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </nav>
              </div>
            </div>

            <div className="col-sm-12 col-md-4  col-lg-4">
              <div className="header_right">
                <div className="button_outer">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a className="search_icon" href="#" data-bs-toggle="modal" data-bs-target="#exampleModal" onClick={closeNavbar}><i className="ri-search-line"></i></a>
                  <button
                    type="button"
                    className="login_btn deposit-btn"
                    onClick={() => {
                      closeNavbar();
                      toggleDepositSidebar();
                    }}
                  >
                    Deposit
                  </button>

                  <div
                    className={`assets_dropdown_menu wallet_tb dropdown header-user-dd ${openDropdown === "wallet" ? "show" : ""}`}
                    onMouseEnter={() => openDropdownHover("wallet")}
                    onMouseLeave={closeDropdownHover}
                  >
                    <button
                      type="button"
                      className={`wallet_tb_btn ${openDropdown === "wallet" || isAssetsActive ? "active" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openDropdown === "wallet"}
                      aria-label="Wallet"
                      onMouseEnter={() => openDropdownHover("wallet")}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.innerWidth < 1200) {
                          toggleDropdown("wallet");
                        } else {
                          openAsserOverview();
                          setOpenDropdown(null);
                        }
                      }}
                    >
                      Assets <i className="ri-arrow-down-s-fill" aria-hidden="true" />
                    </button>

                    <ul
                      className={`dropdown-menu wallet-dd-menu ${openDropdown === "wallet" ? "show" : ""}`}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={() => openDropdownHover("wallet")}
                      onMouseLeave={closeDropdownHover}
                      role="menu"
                    >
                      <li className="wallet-dd-menu__overview-li">
                        <div className="dropdown-item wallet-dd-menu__overview" onClick={(e) => e.stopPropagation()} role="group">
                          <div className="wallet-dd-menu__overview-top">

                            <div className="wallet-dd-menu__overview-top-left d-flex align-items-center justify-content-between gap-2">

                              <button
                                type="button"
                                className="wallet-dd-menu__overview-title-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentPage("Overview");
                                  navigate("/user_profile/asset_overview");
                                }}
                              >
                                Overview
                              </button>
                              <button
                                type="button"
                                className="wallet-dd-menu__icon-btn"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setShowBalance((v) => !v);
                                }}
                                aria-label={showBalance ? "Hide balance" : "Show balance"}
                              >
                                <i className={showBalance ? "ri-eye-line" : "ri-eye-close-line"} aria-hidden="true" />
                              </button>

                            </div>

                            <div className="wallet-dd-menu__overview-actions">

                              <button
                                type="button"
                                className="wallet-dd-menu__icon-btn"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setCurrentPage("Overview");
                                  navigate("/user_profile/asset_overview");
                                }}
                                aria-label="Account summary"
                              >
                                <img src="/images/security/overview_file.svg" alt="Account Summary" />
                              </button>
                            </div>
                          </div>
                          {/* <div className="wallet-dd-menu__overview-divider" aria-hidden="true" /> */}
                          <button
                            type="button"
                            className="wallet-dd-menu__balance-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPage("Overview");
                              navigate("/user_profile/asset_overview");
                            }}
                          >
                            <span className="wallet-dd-menu__balance-num">{overviewBalanceNum}</span>
                            {showBalance ? <span className="wallet-dd-menu__balance-unit">USDT</span> : null}
                          </button>
                        </div>
                      </li>

                      {HEADER_ASSETS_WALLET_LINKS.map((it) => (
                        <li key={it.key}>
                          <Link
                            to={it.to}
                            className="dropdown-item wallet-dd-menu__account-row"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPage(it.label);
                              setOpenDropdown(null);
                              closeNavbar();
                            }}
                          >
                            {it.iconSrc ? (
                              <>
                                <img
                                  src={it.iconSrc}
                                  alt=""
                                  className={`wallet-dd-menu__row-icon wallet-dd-menu__row-icon--pocket ${it.iconSrcLight ? "dark_img" : ""}`}
                                  width={22}
                                  height={22}
                                />
                                {it.iconSrcLight && (
                                  <img
                                    src={it.iconSrcLight}
                                    alt=""
                                    className="wallet-dd-menu__row-icon wallet-dd-menu__row-icon--pocket light_img"
                                    width={22}
                                    height={22}
                                  />
                                )}
                              </>
                            ) : (
                              <i className={`${it.icon} wallet-dd-menu__row-icon-fallback`} aria-hidden="true" />
                            )}
                            <span className="dropdown-item-content">
                              <span className="dropdown-item-text">{it.label}</span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="order_now_hdr header-user-dd"
                    onMouseEnter={() => openDropdownHover("orders")}
                    onMouseLeave={closeDropdownHover}
                  >
                    <button
                      type="button"
                      className={`orderhder_btn ${openDropdown === "orders" || isOrdersActive ? "orderhder_btn--active" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openDropdown === "orders"}
                      onClick={() => {
                        if (window.innerWidth < 1200) {
                          toggleDropdown("orders");
                        } else {
                          goOrdersIndexFromHeader();
                        }
                      }}
                    >
                      Orders <i className="ri-arrow-down-s-fill" aria-hidden="true" />
                    </button>
                    <ul
                      className={`dropdown-menu header-user-dd__menu ${openDropdown === "orders" ? "show" : ""}`}
                      role="menu"
                      onMouseEnter={() => openDropdownHover("orders")}
                      onMouseLeave={closeDropdownHover}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {HEADER_ORDER_MENU_ITEMS.map((item) => (
                        <li key={item.to} role="none">
                          <Link
                            role="menuitem"
                            className="dropdown-item header-user-dd__item"
                            to={item.to}
                            onClick={() => {
                              setCurrentPage(item.page);
                              closeNavbar();
                              closeDepositSidebar();
                            }}
                          >
                            {item.iconSrc && (
                              <img className="header-user-dd__icon dark_img" src={item.iconSrc} alt="" width={18} height={18} />
                            )}
                            {item.iconSrcLight && (
                              <img className="header-user-dd__icon light_img" src={item.iconSrcLight} alt="" width={18} height={18} />
                            )}
                            <span>{item.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="user_login dashbtn header-user-dd header-user-dd--profile"
                    onMouseEnter={() => openDropdownHover("profile")}
                    onMouseLeave={closeDropdownHover}
                  >
                    <Link
                      to="/user_profile/dashboard"
                      className={`header-user-dd__trigger ${isDashboardActive ? "header-user-dd__trigger--active" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openDropdown === "profile"}
                      onClick={(e) => {
                        closeNavbar();
                        if (window.innerWidth < 1200) {
                          e.preventDefault();
                          toggleDropdown("profile");
                        }
                      }}
                    >
                      <img
                        src={
                          userDetails?.profilepicture
                            ? (resolveUserAvatarUrl(userDetails.profilepicture) || "/images/userheader.svg")
                            : "/images/userheader.svg"
                        }
                        alt="Dashboard"
                        style={{ borderRadius: "50%" }}
                        onError={(e) => {
                          e.target.src = "/images/userheader.svg";
                        }}
                      />
                    </Link>

                    <ul

                      className={`dropdown-menu header-user-dd__menu ${openDropdown === "profile" ? "show" : ""}`}
                      role="menu"
                      onMouseEnter={() => openDropdownHover("profile")}
                      onMouseLeave={closeDropdownHover}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="header-user-dd__mobile-close d-xl-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(null);
                        }}
                        aria-label="Close"
                      >
                        <i className="ri-close-line" aria-hidden="true" />
                      </button>


                      <div className="mobile_data_menu">
                        <div className="user_profile">

                          <div className="user_left_img">
                            <div className="user_img">
                              <img
                                src={
                                  userDetails?.profilepicture
                                    ? (resolveUserAvatarUrl(userDetails.profilepicture) || "/images/userheader.svg")
                                    : "/images/userheader.svg"
                                }
                                alt="User profile"
                                onError={(e) => {
                                  e.target.src = "/images/userheader.svg";
                                }}
                              />                               </div>
                          </div>
                          <div className="user_profile_cnt">
                            <h3>{maskedProfileEmail}</h3>
                            <div className="profile_id">
                              <span>UID :</span>
                              <div className="uid d-flex align-items-center">
                                {profileUid}
                                <button
                                  type="button"
                                  className="bg-transparent border-0 p-0 m-1"
                                  aria-label="Copy UID"
                                  title="Copy UID"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleCopyProfileUid();
                                  }}
                                >
                                  <img src="/images/uid_icon.svg" alt="Copy UID" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <button type="button" className="mobile_deposit_btn">Deposit</button>



                        <div className={`assets_dropdown_menu wallet_tb dropdown header-user-dd ${mobileProfileSection === "assets" ? "show" : ""}`}>
                          <button
                            type="button"
                            className={`wallet_tb_btn ${mobileProfileSection === "assets" || isAssetsActive ? "active" : ""}`}
                            aria-haspopup="menu"
                            aria-expanded={mobileProfileSection === "assets"}
                            aria-label="Wallet"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.innerWidth < 1200) {
                                setMobileProfileSection((s) => (s === "assets" ? null : "assets"));
                              }
                            }}
                          >
                            <span>
                              <img className='suborder_img dark_img2' src="/images/affiliate-icon.svg" alt="" loading="lazy" />
                              <img className='suborder_img light_img2' src="/images/security/affiliate-icon_light2.svg" alt="" loading="lazy" />
                              Assets
                            </span> <i class="ri-arrow-down-s-line"></i>
                          </button>

                          <ul
                            className={`dropdown-menu wallet-dd-menu ${mobileProfileSection === "assets" ? "show" : ""}`}
                            onClick={(e) => e.stopPropagation()}
                            role="menu"
                          >
                            <li className="wallet-dd-menu__overview-li">
                              <div className="dropdown-item wallet-dd-menu__overview" onClick={(e) => e.stopPropagation()} role="group">
                                <div className="wallet-dd-menu__overview-top">

                                  <div className="wallet-dd-menu__overview-top-left d-flex align-items-center justify-content-between gap-2">

                                    <button
                                      type="button"
                                      className="wallet-dd-menu__overview-title-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentPage("Overview");
                                        navigate("/user_profile/asset_overview");
                                      }}
                                    >
                                      Overview
                                    </button>
                                    <button
                                      type="button"
                                      className="wallet-dd-menu__icon-btn"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setShowBalance((v) => !v);
                                      }}
                                      aria-label={showBalance ? "Hide balance" : "Show balance"}
                                    >
                                      <i className={showBalance ? "ri-eye-line" : "ri-eye-close-line"} aria-hidden="true" />
                                    </button>

                                  </div>

                                  <div className="wallet-dd-menu__overview-actions">

                                    <button
                                      type="button"
                                      className="wallet-dd-menu__icon-btn"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setCurrentPage("Overview");
                                        navigate("/user_profile/asset_overview");
                                      }}
                                      aria-label="Account summary"
                                    >
                                      <img src="/images/security/overview_file.svg" alt="Account Summary" />
                                    </button>
                                  </div>
                                </div>
                                {/* <div className="wallet-dd-menu__overview-divider" aria-hidden="true" /> */}
                                <button
                                  type="button"
                                  className="wallet-dd-menu__balance-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentPage("Overview");
                                    navigate("/user_profile/asset_overview");
                                  }}
                                >
                                  <span className="wallet-dd-menu__balance-num">{overviewBalanceNum}</span>
                                  {showBalance ? <span className="wallet-dd-menu__balance-unit">USDT</span> : null}
                                </button>
                              </div>
                            </li>

                            {HEADER_ASSETS_WALLET_LINKS.map((it) => (
                              <li key={it.key}>
                                <Link
                                  to={it.to}
                                  className="dropdown-item wallet-dd-menu__account-row"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentPage(it.label);
                                    setOpenDropdown(null);
                                    closeNavbar();
                                  }}
                                >
                                  {it.iconSrc ? (
                                    <>
                                      <img
                                        src={it.iconSrc}
                                        alt=""
                                        className={`wallet-dd-menu__row-icon wallet-dd-menu__row-icon--pocket ${it.iconSrcLight ? "dark_img" : ""}`}
                                        width={22}
                                        height={22}
                                      />
                                      {it.iconSrcLight && (
                                        <img
                                          src={it.iconSrcLight}
                                          alt=""
                                          className="wallet-dd-menu__row-icon wallet-dd-menu__row-icon--pocket light_img"
                                          width={22}
                                          height={22}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <i className={`${it.icon} wallet-dd-menu__row-icon-fallback`} aria-hidden="true" />
                                  )}
                                  <span className="dropdown-item-content">
                                    <span className="dropdown-item-text">{it.label}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div
                          className="order_now_hdr header-user-dd"
                        >
                          <button
                            type="button"
                            className={`orderhder_btn ${(mobileProfileSection === "orders" || isOrdersActive) ? " orderhder_btn--active" : ""}`}
                            aria-haspopup="menu"
                            aria-expanded={mobileProfileSection === "orders"}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.innerWidth < 1200) {
                                setMobileProfileSection((s) => (s === "orders" ? null : "orders"));
                              }
                            }}
                          >
                            <span>
                              <img className='suborder_img dark_img2' src="/images/security/order_hdr_icon.svg" alt="" loading="lazy" />
                              <img className='suborder_img  light_img2' src="/images/security/order_hdr_icon_light.svg" alt="" loading="lazy" />
                              Orders
                            </span> <i class="ri-arrow-down-s-line"></i>
                          </button>
                          <ul
                            className={`dropdown-menu header-user-dd__menu ${mobileProfileSection === "orders" ? "show" : ""}`}
                            role="menu"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {HEADER_ORDER_MENU_ITEMS.map((item) => (
                              <li key={item.to} role="none">
                                <Link
                                  role="menuitem"
                                  className="dropdown-item header-user-dd__item"
                                  to={item.to}
                                  onClick={() => {
                                    setCurrentPage(item.page);
                                    closeNavbar();
                                    closeDepositSidebar();
                                  }}
                                >
                                  {item.iconSrc && (
                                    <img className="header-user-dd__icon dark_img" src={item.iconSrc} alt="" width={18} height={18} />
                                  )}
                                  {item.iconSrcLight && (
                                    <img className="header-user-dd__icon light_img" src={item.iconSrcLight} alt="" width={18} height={18} />
                                  )}
                                  <span>{item.label}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/dashboard"
                          onClick={() => {
                            setCurrentPage("Overview");
                            closeNavbar();
                          }}
                        >

                          <img className='header-user-dd__icon dark_img' src="/images/security/dashboard_hdr_icon.svg" alt="Overview" />
                          <img className='header-user-dd__icon light_img' src="/images/security/dashboard_hdr_icon_light.svg" alt="Overview" />
                          <span>Overview</span>
                        </Link>
                      </li>
                      <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/security"
                          onClick={() => {
                            setCurrentPage("Security");
                            closeNavbar();
                          }}
                        >
                          <img className='header-user-dd__icon dark_img' src="/images/security/dashboard_hdr_icon2.svg" alt="Security" />
                          <img className='header-user-dd__icon light_img' src="/images/security/dashboard_hdr_icon2_light.svg" alt="Security" />
                          <span>Security</span>
                        </Link>
                      </li>
                      <li role="none">
                        {/* Opens the KYC VARA modal on the current page. */}
                        <button
                          type="button"
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          onClick={() => {
                            closeNavbar();
                            openKycVerification();
                          }}
                        >
                          <img className='header-user-dd__icon dark_img' src="/images/security/dashboard_hdr_icon3.svg" alt="Identification" />
                          <img className='header-user-dd__icon light_img' src="/images/security/dashboard_hdr_icon3_light.svg" alt="Identification" />
                          <span>Identification</span>
                        </button>
                      </li>
                      {/* <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/dashboard"
                          onClick={() => {
                            setCurrentPage("Overview");
                            closeNavbar();
                          }}
                        >
                          <img className='header-user-dd__icon' src="/images/security/dashboard_hdr_icon4.svg" alt="Overview" />
                          <span>Subaccounts</span>
                        </Link>
                      </li> */}
                      {/* <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/dashboard"
                          onClick={() => {
                            setCurrentPage("Overview");
                            closeNavbar();
                          }}
                        >
                          <img className='header-user-dd__icon' src="/images/security/dashboard_hdr_icon5.svg" alt="Overview" />
                          <span>API Key Managment</span>
                        </Link>
                      </li> */}
                      {/* <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/dashboard"
                          onClick={() => {
                            setCurrentPage("Overview");
                            closeNavbar();
                          }}
                        >
                          <img className='header-user-dd__icon' src="/images/security/dashboard_hdr_icon6.svg" alt="Overview" />
                          <span>Vouchers</span>
                        </Link>
                      </li> */}
                      <li role="none">
                        <Link
                          role="menuitem"
                          className="dropdown-item header-user-dd__item"
                          to="/user_profile/my_tickets"
                          onClick={() => {
                            setCurrentPage("My Tickets");
                            closeNavbar();
                          }}
                        >
                          <img className='header-user-dd__icon dark_img' src="/images/security/dashboard_hdr_icon7.svg" alt="My Tickets" />
                          <img className='header-user-dd__icon light_img' src="/images/security/dashboard_hdr_icon7_light.svg" alt="My Tickets" />
                          <span>My Tickets</span>
                        </Link>
                      </li>
                      <li role="none" className="header-user-dd__logout-li">
                        <button type="button" className="header-user-dd__logout" role="menuitem" onClick={logOut}>
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </div>

                </div>
                <div className="icon_notificaton_r">
                  <ul>
                    <li
                      ref={notificationRef}
                      className={`notification_nav ${showNotification ? 'open' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/user_profile/notification");
                        setShowNotification(false);
                      }}
                      onMouseEnter={openNotificationHover}
                      onMouseLeave={closeNotificationHover}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate("/user_profile/notification"); setShowNotification(false); } }}
                      aria-label="Notifications"
                      aria-expanded={showNotification}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none">
                        <path d="M12.75 5.69531V10.5234H11.25V5.69531H12.75ZM4.7344 13.0312C4.32815 13.7188 4.0508 14.4531 3.90236 15.2344C3.75393 16.0156 3.74221 16.8047 3.86721 17.6016L4.00783 18.3984H19.9922L20.1563 17.6016C20.2969 16.8047 20.2969 16.0156 20.1563 15.2344C20.0156 14.4531 19.7344 13.7188 19.3125 13.0312C19.1406 12.75 18.9922 12.3867 18.8672 11.9414C18.7422 11.4961 18.6797 11.1016 18.6797 10.7578V8.64844C18.6797 7.46094 18.375 6.34375 17.7656 5.29688C17.1719 4.29688 16.375 3.5 15.375 2.90625C14.3438 2.29688 13.2266 1.99219 12.0235 1.99219C10.8203 1.99219 9.69533 2.29688 8.64846 2.90625C7.64846 3.5 6.85158 4.29688 6.25783 5.29688C5.66408 6.32812 5.36721 7.44531 5.36721 8.64844V10.7578C5.36721 11.1016 5.30471 11.4961 5.17971 11.9414C5.05471 12.3867 4.90627 12.75 4.7344 13.0312ZM17.1797 8.67188V10.7578C17.1797 11.2266 17.2617 11.75 17.4258 12.3281C17.5899 12.9062 17.7891 13.3984 18.0235 13.8047C18.6016 14.7578 18.836 15.7891 18.7266 16.8984H5.2969C5.20315 15.7891 5.44533 14.7578 6.02346 13.8047C6.25783 13.3984 6.45705 12.9102 6.62111 12.3398C6.78518 11.7695 6.86721 11.2422 6.86721 10.7578V8.64844C6.86721 7.97656 6.99611 7.32422 7.25393 6.69141C7.51174 6.05859 7.88283 5.5 8.36721 5.01562C8.85158 4.53125 9.41018 4.15625 10.043 3.89062C10.6758 3.625 11.336 3.49219 12.0235 3.49219C12.9453 3.49219 13.8008 3.72656 14.5899 4.19531C15.3789 4.66406 16.0078 5.29297 16.4766 6.08203C16.9453 6.87109 17.1797 7.73438 17.1797 8.67188ZM9.75002 19.0078C9.75002 19.3047 9.80861 19.5898 9.9258 19.8633C10.043 20.1367 10.2071 20.3789 10.418 20.5898C10.6289 20.8008 10.8711 20.9648 11.1446 21.082C11.418 21.1992 11.7031 21.2578 12 21.2578C12.625 21.2422 13.1563 21.0156 13.5938 20.5781C14.0313 20.1406 14.25 19.6172 14.25 19.0078H15.75C15.75 19.6797 15.5821 20.3008 15.2461 20.8711C14.9102 21.4414 14.4531 21.8984 13.875 22.2422C13.2969 22.5859 12.6719 22.7578 12 22.7578C11.5 22.7578 11.0196 22.6602 10.5586 22.4648C10.0977 22.2695 9.69533 21.9961 9.35158 21.6445C9.00783 21.293 8.7383 20.8867 8.54299 20.4258C8.34768 19.9648 8.25002 19.4922 8.25002 19.0078H9.75002Z" fill="white" />
                      </svg>
                      {notificationCounts?.unseen > 0 && (
                        <span className="notification_count">{notificationCounts.unseen}</span>
                      )}
                      <div
                        className="annousment_s header_notification agce-hdr-notification"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseEnter={openNotificationHover}
                        onMouseLeave={closeNotificationHover}
                        role="presentation"
                      >
                        <div className="agce-hdr-notification__toolbar">
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span className="agce-hdr-notification__toolbar-title">Notifications</span>
                            {notificationCounts?.unseen > 0 ? (
                              <span className="agce-hdr-notification__pill" aria-live="polite">
                                {notificationCounts.unseen} new
                              </span>
                            ) : null}
                          </div>
                          <div className="more_btn agce-hdr-notification__more">
                            <Link
                              to="/user_profile/notification"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentPage("Notification");
                                setShowNotification(false);
                              }}
                            >
                              More
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-right ms-1" viewBox="0 0 16 16">
                                <path
                                  fillRule="evenodd"
                                  d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"
                                />
                              </svg>
                            </Link>
                          </div>
                        </div>

                        <div className="agce-hdr-notification__scroll">
                          {notifications?.length > 0 ? (
                            notifications.map((item, index) => {
                              const unread = !item?.isSeen;
                              return (
                                <button
                                  key={item?._id || index}
                                  type="button"
                                  className={`agce-hdr-notification__row ${unread ? "is-unread" : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleHeaderNotifyItemNavigate(item);
                                  }}
                                >
                                  <span className="agce-hdr-notification__row-icon">
                                    <img src="/images/notification_icon.svg" alt="" width={22} height={22} />
                                  </span>
                                  <span className="agce-hdr-notification__row-body">
                                    <span className="agce-hdr-notification__row-title">
                                      {truncateNotificationPreview(item?.title, 52)}
                                    </span>
                                    {item?.message != null && String(item.message).trim() !== "" ? (
                                      <span className="agce-hdr-notification__row-msg">
                                        {truncateNotificationPreview(item.message, 90)}
                                      </span>
                                    ) : null}
                                  </span>
                                  {unread ? <span className="agce-hdr-notification__dot" aria-hidden="true" /> : null}
                                </button>
                              );
                            })
                          ) : (
                            <div className="agce-hdr-notification__empty favouriteData">
                              <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width={72} height={72} alt="" />
                              <img src="/images/no_data_vector_light.png" className="img-fluid light_img" width={72} height={72} alt="" />
                              <p className="agce-hdr-notification__empty-text">No notifications yet</p>
                            </div>
                          )}
                        </div>

                      </div>
                    </li>

                    {/* <li className='downloadtabs'>
                      <img src="/images/download_icon2.svg" alt="download" />
                      <div className='scantophdr'>
                        <div className='qrcode'>
                          <div className="scan_img"><img src="/images/scan.png" alt="scan" /></div>
                          <p>Scan to Download App iOS & Android</p>
                          <button className='btn'>Download</button>
                        </div>
                      </div>
                    </li> */}
                    <li
                      className="themetbs"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        handleTheme();
                        setThemeUpdated((prev) => !prev);
                      }}
                      title="Toggle Theme"
                    >
                      <i
                        className={newStoredTheme === "dark" ? "ri-sun-line dark_img" : "ri-moon-line light_img"}
                        aria-hidden="true"
                      />
                    </li>

                    {/* <li className="themetbs"><img src="/images/themeicon.svg" alt="theme" /></li> */}
                  </ul>
                </div>
              </div>

            </div>
          </div>

        </div>
      </header>
      <div className={`sidebar_deposit ${depositSidebarOpen ? "open" : ""}`} id="depositSidebar">
        <button type="button" className="close-btn" onClick={closeDepositSidebar}>&times;</button>
        <h2>I have crypto assets</h2>
        <div className="deposit_list">
          <Link to='/asset_managemnet/deposit' className="deposit_option" onClick={closeDepositSidebar}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none"><path d="M18.75 10.25C18.5858 10.2499 18.4232 10.2822 18.2715 10.345C18.1198 
            10.4077 17.982 10.4998 17.8659 10.6159C17.7498 10.732 17.6577 10.8698 17.595 11.0215C17.5322 11.1732 17.4999 11.3358 17.5 11.5V15.25H2.5V11.5C2.5 11.1685 2.3683 10.8505 
            2.13388 10.6161C1.89946 10.3817 1.58152 10.25 1.25 10.25C0.91848 10.25 0.600537 10.3817 0.366117 10.6161C0.131696 10.8505 2.00941e-07 11.1685 2.00941e-07 11.5V16.5C-9.29127e-05 
            16.6642 0.0321757 16.8268 0.0949611 16.9785C0.157747 17.1302 0.249817 17.268 0.365909 17.3841C0.482001 17.5002 0.619837 17.5923 0.771536 17.655C0.923235 17.7178 1.08582 17.7501 
            1.25 17.75H18.75C18.9142 17.7501 19.0768 17.7178 19.2285 17.655C19.3802 17.5923 19.518 17.5002 19.6341 17.3841C19.7502 17.268 19.8423 17.1302 19.905 16.9785C19.9678 16.8268 
            20.0001 16.6642 20 16.5V11.5C20.0001 11.3358 19.9678 11.1732 19.905 11.0215C19.8423 10.8698 19.7502 10.732 19.6341 10.6159C19.518 10.4998 19.3802 10.4077 19.2285 10.345C19.0768
             10.2822 18.9142 10.2499 18.75 10.25Z" fill="white"></path>
              <path d="M9.11558 12.3838C9.23162 12.4999 9.3694 12.592 9.52104 12.6548C9.67268 12.7177 9.83522 12.75 9.99937 12.75C10.1635 12.75 10.3261 12.7177 10.4777 12.6548C10.6293 
            12.592 10.7671 12.4999 10.8832 12.3838L14.6332 8.63379C14.8665 8.39917 14.9972 8.0816 14.9968 7.75072C14.9963 7.41984 14.8647 7.10264 14.6307 6.86866C14.3967 6.63469 
            14.0795 6.50305 13.7486 6.50259C13.4178 6.50212 13.1002 6.63289 12.8656 6.86621L11.2494 8.48242V1.5C11.2494 1.16848 11.1177 0.850537 10.8833 0.616117C10.6488 0.381696 
            10.3309 0.25 9.99937 0.25C9.66785 0.25 9.34991 0.381696 9.11549 0.616117C8.88106 0.850537 8.74937 1.16848 8.74937 1.5V8.48242L7.13316 6.86621C6.89854 6.63289 6.58097 
            6.50212 6.25009 6.50259C5.9192 6.50305 5.602 6.63469 5.36803 6.86866C5.13406 7.10264 5.00241 7.41984 5.00195 7.75072C5.00149 8.0816 5.13226 8.39917 
            5.36558 8.63379L9.11558 12.3838Z" fill="white"></path></svg>
            <div className="deposit_cnt">
              Deposit Crypto
              <p>Send crypto to your AGCX Account</p>
            </div>
          </Link>
        </div>
        <h2 className='mt-4'>Withdraw crypto assets</h2>
        <div className="deposit_list">
          <Link to='/asset_managemnet/withdraw' className="deposit_option" onClick={closeDepositSidebar}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
              <path d="M18.75 10.25C18.5858 10.2499 18.4232 10.2822 18.2715 10.345C18.1198 10.4077 17.982 10.4998 17.8659 10.6159C17.7498 10.732
            17.6577 10.8698 17.595 11.0215C17.5322 11.1732 17.4999 11.3358 17.5 11.5V15.25H2.5V11.5C2.5 11.1685 2.3683 10.8505 2.13388 10.6161C1.89946
             10.3817 1.58152 10.25 1.25 10.25C0.91848 10.25 0.600537 10.3817 0.366117 10.6161C0.131696 10.8505 2.00941e-07 11.1685 2.00941e-07
              11.5V16.5C-9.29127e-05 16.6642 0.0321757 16.8268 0.0949611 16.9785C0.157747 17.1302 0.249817 17.268 0.365909 17.3841C0.482001 17.5002
               0.619837 17.5923 0.771536 17.655C0.923235 17.7178 1.08582 17.7501 1.25 17.75H18.75C18.9142 17.7501 19.0768 17.7178 19.2285 17.655C19.3802
                17.5923 19.518 17.5002 19.6341 17.3841C19.7502 17.268 19.8423 17.1302 19.905 16.9785C19.9678 16.8268 20.0001 16.6642 20 16.5V11.5C20.0001
                 11.3358 19.9678 11.1732 19.905 11.0215C19.8423 10.8698 19.7502 10.732 19.6341 10.6159C19.518 10.4998 19.3802 10.4077 19.2285 10.345C19.0768
                  10.2822 18.9142 10.2499 18.75 10.25Z" fill="white"></path><path d="M7.13316 6.13379L8.74937 4.51758V11.5C8.74937 11.8315 8.88106
                   12.1495 9.11549 12.3839C9.34991 12.6183 9.66785 12.75 9.99937 12.75C10.3309 12.75 10.6488 12.6183 10.8833 12.3839C11.1177 12.1495
                    11.2494 11.8315 11.2494 11.5V4.51758L12.8656 6.13379C13.1002 6.36711 13.4178 6.49787 13.7486 6.49741C14.0795 6.49695 14.3967 6.3653
                     14.6307 6.13133C14.8647 5.89736 14.9963 5.58016 14.9968 5.24928C14.9972 4.91839 14.8665 4.60083 14.6332 4.36621L10.8832 0.616206C10.7671
                      0.500108 10.6293 0.408011 10.4777 0.345176C10.3261 0.282341 10.1635 0.25 9.99937 0.25C9.83522 0.25 9.67268 0.282341 9.52104
                       0.345176C9.3694 0.408011 9.23162 0.500108 9.11558 0.616206L5.36558 4.36621C5.13226 4.60083 5.00149 4.91839 5.00195 5.24928C5.00241
                        5.58016 5.13406 5.89736 5.36803 6.13133C5.602 6.3653 5.9192 6.49695
            6.25009 6.49741C6.58097 6.49787 6.89854 6.36711 7.13316 6.13379Z" fill="white"></path></svg>
            <div className="deposit_cnt">
              Withdraw Crypto
              <p>Withdraw crypto to your preferred wallet</p>
            </div>
          </Link>
        </div>
        <h2 className='mt-4'> Deposit Fiat assets</h2>
        <div className="deposit_list">
          <Link to='/asset_managemnet/deposit_fiat' className="deposit_option" onClick={closeDepositSidebar}>
            <img className="dark_img" src="/images/deposit_fiat_icon.png" alt="" width={20} height={20} />
            <img className="light_img" src="/images/deposit_fiat_icon_light.png" alt="" width={20} height={20} />
            <div className="deposit_cnt">
              Deposit Fiat
              <p>Deposit fiat assets to your AGCX account</p>
            </div>
          </Link>
        </div>

        <h2 className='mt-4'>Withdraw Fiat assets</h2>
        <div className="deposit_list">
          <Link to='/asset_managemnet/withdraw_fiat' className="deposit_option" onClick={closeDepositSidebar}>
            <img className="dark_img" src="/images/withdrawal_fiat_icon.png" alt="" width={20} height={20} />
            <img className="light_img" src="/images/withdrawal_fiat_icon_light.png" alt="" width={20} height={20} />
            <div className="deposit_cnt">
              Withdraw Fiat
              <p>Withdraw fiat funds to your preferred bank account</p>
            </div>
          </Link>
        </div>


      </div>
    </>
  )
}

export default AuthHeader;
